import { useState } from 'react';
import React from 'react';
import { useTranslation } from 'react-i18next';

import { BookImage, Brain, ChartLine, CircleDollarSign, Inbox } from 'lucide-react';

import { Typography } from '@components/typography';

import { isMobileDevice } from '@globalUtils/environment';

import MainPicBotImg from '@assets/mainPicBot.avif';
import MainPicCatalogImg from '@assets/mainPicCatalog.avif';
import MainPicInboxImg from '@assets/mainPicInbox.avif';
import MainPicMetricsImg from '@assets/mainPicMetrics.avif';
import MainPicPaymentImg from '@assets/mainPicPayment.avif';

const CarouselButton = ({
  active,
  onClick,
  icon,
  label,
}: {
  active: boolean;
  onClick: () => void;
  icon: React.JSX.Element;
  label: string;
}) => {
  return (
    <div className="cursor-pointer! flex flex-col items-center justify-end w-[100px] mt-[10vh]">
      <div className={`${active ? 'border-black p-[1px]' : ''} border rounded-[13px] shadow-xl`}>
        <button
          onClick={onClick}
          aria-label={label}
          className={`${active ? 'bg-black border-black text-white! w-[50px] h-[50px] shadow-xl' : 'bg-gray-100 border-white text-gray-600! w-[50px] h-[50px]'} rounded-[12px] border flex items-center justify-center cursor-pointer! p-0`}
        >
          {icon}
        </button>
      </div>

      <Typography
        variant="caption"
        className={`${active ? 'font-bold text-black' : 'text-gray-500 font-semibold'} cursor-pointer! mt-3`}
      >
        {label}
      </Typography>
    </div>
  );
};

export const HeroCarousel = () => {
  const [activeItem, setActiveItem] = useState('inbox');

  const { t } = useTranslation();

  const items = [
    {
      id: 'catalog',
      icon: <BookImage />,
      label: t('Catalog'),
    },
    {
      id: 'bot',
      icon: <Brain />,
      label: t('Bot'),
    },
    {
      id: 'inbox',
      icon: <Inbox />,
      label: t('Inbox'),
    },
    {
      id: 'payments',
      icon: <CircleDollarSign />,
      label: t('Payments'),
    },
    {
      id: 'metrics',
      icon: <ChartLine />,
      label: t('Metrics'),
    },
  ];

  const pics: Record<string, string> = {
    catalog: MainPicCatalogImg,
    inbox: MainPicInboxImg,
    bot: MainPicBotImg,
    payments: MainPicPaymentImg,
    metrics: MainPicMetricsImg,
  };

  return (
    <div className="w-full flex flex-col items-center">
      <div className="relative lg:mt-6 z-50 w-full lg:w-[70vw] flex justify-evenly items-end">
        {items.map((item) => {
          return (
            <React.Fragment key={item.id}>
              <CarouselButton
                label={item.label}
                icon={item.icon}
                active={item.id === activeItem}
                onClick={() => {
                  setActiveItem(item.id);
                }}
              />
            </React.Fragment>
          );
        })}
      </div>

      <div className="relative pb-2 top-[5vh] lg:top-[5vh] bg-white">
        <div className="border-1 border-gray-100 rounded-xl">
          <div className="rounded-xl p-2 lg:p-4 border-1 border-white shadow-lg">
            <div className="overflow-hidden rounded-md border-gray-100 border-1">
              <img
                loading="lazy"
                className="w-[90vw]"
                src={pics[activeItem]}
                alt={`Closer ${items.find((item) => item.id === activeItem)?.label || activeItem} feature screenshot`}
              />
            </div>
          </div>
        </div>

        {!isMobileDevice() && (
          <div className="pointer-events-none absolute inset-x-0 -bottom-8 h-[15vh] bg-gradient-to-t from-white via-white/90 to-transparent"></div>
        )}
      </div>
    </div>
  );
};
